/**
 * AttachmentMenu
 *
 * Paperclip button that opens a popover of attachment options.
 *
 * Options are driven by an `items` array so new message types (location,
 * event, album, ...) register as items instead of hard-coded buttons:
 *   { id, icon, label, iconClass?, accept?, onSelect }
 * When `accept` is set the item opens a file picker and calls
 * `onSelect(file)`; otherwise `onSelect()` fires directly.
 *
 * When `items` is omitted, a default set is built from the legacy callback
 * props (onSelectImage / onSelectFile / onStartVoice / onStartSmartMessage)
 * so existing callers keep working unchanged.
 */

import { useState, useRef, useMemo } from 'react';
import {
  PaperClipIcon,
  PhotoIcon,
  DocumentIcon,
  MicrophoneIcon,
  ChatBubbleLeftRightIcon
} from '@heroicons/react/24/outline';

export default function AttachmentMenu({
  items,
  onSelectImage,
  onSelectFile,
  onStartVoice,
  onStartSmartMessage
}) {
  const [open, setOpen] = useState(false);
  const fileInputRef = useRef(null);
  const pendingItemRef = useRef(null);

  const defaultItems = useMemo(() => [
    {
      id: 'photo',
      icon: PhotoIcon,
      label: 'Photo',
      iconClass: 'text-success',
      accept: 'image/*',
      onSelect: onSelectImage
    },
    {
      id: 'file',
      icon: DocumentIcon,
      label: 'File',
      iconClass: 'text-info',
      accept: '*/*',
      onSelect: onSelectFile
    },
    {
      id: 'voice',
      icon: MicrophoneIcon,
      label: 'Voice Note',
      iconClass: 'text-error',
      onSelect: onStartVoice
    },
    {
      id: 'smart-message',
      icon: ChatBubbleLeftRightIcon,
      label: 'Smart Message',
      iconClass: 'text-primary',
      onSelect: onStartSmartMessage
    }
  ], [onSelectImage, onSelectFile, onStartVoice, onStartSmartMessage]);

  const menuItems = items || defaultItems;

  const handleItemClick = (item) => {
    if (item.accept) {
      // File-backed item: open the picker; onSelect fires with the file.
      pendingItemRef.current = item;
      if (fileInputRef.current) {
        fileInputRef.current.accept = item.accept;
        fileInputRef.current.click();
      }
    } else {
      item.onSelect?.();
      setOpen(false);
    }
  };

  const handleFileChosen = (e) => {
    const file = e.target.files?.[0];
    const item = pendingItemRef.current;
    pendingItemRef.current = null;
    if (file && item) {
      item.onSelect?.(file);
      setOpen(false);
    }
    // Reset input so same file can be selected again
    e.target.value = '';
  };

  return (
    <div className="relative">
      <button
        type="button"
        className="btn btn-ghost btn-sm btn-circle"
        onClick={() => setOpen(!open)}
        title="Attach"
      >
        <PaperClipIcon className="w-5 h-5" />
      </button>

      {open && (
        <>
          {/* Backdrop */}
          <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} />

          {/* Menu */}
          <div className="absolute bottom-12 left-0 bg-base-100 rounded-lg shadow-xl border border-base-300 py-2 z-20 min-w-[160px]">
            {menuItems.map((item) => {
              const Icon = item.icon;
              return (
                <button
                  key={item.id}
                  className="flex items-center gap-3 w-full px-4 py-2 text-sm hover:bg-base-200 transition-colors"
                  onClick={() => handleItemClick(item)}
                >
                  <Icon className={`w-4 h-4 ${item.iconClass || 'text-primary'}`} />
                  {item.label}
                </button>
              );
            })}
          </div>
        </>
      )}

      {/* Shared hidden file input for file-backed items */}
      <input
        ref={fileInputRef}
        type="file"
        className="hidden"
        onChange={handleFileChosen}
      />
    </div>
  );
}
